import { DataStore, ItemReference, DataModel, Query, QueryResult } from 'src/core/stores/dataStore';
import { FormStore, FormModel } from 'src/core/stores/formStore';
import { repository, AsyncAction, reduce } from 'redux-scaffolding-ts';
import { container } from 'src/inversify.config';
import { Validator } from "lakmus";
import { CommandResult } from '../core/stores/types';
import HttpService from '../core/services/http.service';
import { createPatch } from 'rfc6902';
import { DeleteObject, SaveObject } from 'src/components/collections/grid-table-multiple';
import { ProjectTaskAssignmentTableRequestItem, ProjectTemplateTaskAssignmentSummary } from './projecttaskassignments-store';

export interface ProjectTaskSummary {
    id: string;
    title: string;
    projectTitle: string;
    reference: string;
    projectId: string;
    taskTemplateId?: string;
    taskTemplateTitle?: string;
    sticky: boolean;
    cirEligibility: boolean;
    ciiEligibility: boolean;
    category: string;
    allowedCir: boolean;
    allowedCii: boolean;
    approved: boolean;
}

export interface ProjectTaskItemReference {
    id: string
    title: string
}

export interface ProjectTaskBasicSummary extends ProjectTaskItemReference {
    projectId:string
}

export interface ProjectTaskItem extends NewProjectTaskItem {
    id: number;
}

export interface NewProjectTaskItem {
    title: string;
    description: string;
    reference: string;
    projectId: string;
    taskTemplateId?: string;
}

export interface NewTaskCollectionItem {
    taskTemplateIds: string[]
}

@repository("@@PROJECTTASKS", "projectTasks.summary")
export class ProjectTasksStore extends DataStore<ProjectTaskSummary> {
    baseUrl: string = ``;

    constructor() {
        super('PROJECTTASK', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
        //Add custom reducer to fix sticky error message on update entity
        this.addReducer('TASK_UPDATED', (): any => {
            return {
                onStart: () => ({ ...this.state, isBusy: true }),
                onSuccess: (result: any, item: any) => {
                    return {
                        ...this.state,
                        items: this.state.items.map(o => {
                            if ((o.item as any)[this.rowKey] == ((item as any)[this.rowKey] || result.data.identifier || (this.useTitleKey ? result.data.title : result.data.aggregateRootId || result.data.correlationId))) {
                                o.state = 'Unchanged';
                                o.item = Object.assign(o.item, item);
                                (o.item as any)[this.rowKey] = result.data.identifier || (o.item as any)[this.rowKey];
                            }
                            return o;
                        }),
                        isBusy: false,
                    };
                },
                onError: (error: any) => ({
                    ...this.state, isBusy: false
                })
            };
        }, 'AsyncAction');
    }

    public changeUrlByProject(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}`;
    }

    public changeUrlByEmployee(companyId: string, employeeId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/timesheet/${companyId}/${employeeId}`;
    }

    public changeUrlByCompany(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/tasksfromids`
    }

    public changeUrlMasiveDelete(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/delete-masive`;
    }

    public changeUrlLoadGridAssignmentsData(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/tableAssignmentProjectsTemplates`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectTaskSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectTaskSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }
    public async setEligibility(projectId: string, fieldName: string, value: boolean) {
        var item = this.state.items.firstOrDefault(x => x.item.id == projectId);
        var httpService = this.container.get<HttpService>(HttpService);
        if (item) {
            var partial = Object.assign(Object.assign({}, item.item), { [fieldName]: value });
            return this.dispatchAsync('TASK_UPDATED', httpService.put(`${this.baseUrl}/${encodeURIComponent(projectId)}`, partial), partial);
        }
    }

    public async getAllDeletedAsync(query: Query, data?: any): Promise<QueryResult<ProjectTaskSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectTaskSummary>>(`${this.baseUrl}/deleted?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async restoreTask(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.post(`${this.baseUrl}/${id}/restore`, {});
        return result.data;
    }

    public async softDeleteTask(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.delete(`${this.baseUrl}/${id}/SoftDelete`, {});
        return result.data;
    }

    public async deleteMasive(tasksToDelete:  DeleteObject[]) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.delete(`${this.baseUrl}`, tasksToDelete);
        return result.data;
    }

    public changeDeleteUrl(companyId: string, projectId: string, taskId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}/${taskId}`;
    }

    public async delete() {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.delete(`${this.baseUrl}`);

        return result.data;
    }

    public async getAllAssignments() {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.post(`${this.baseUrl}`, {});
        return result.data;
    }
}

@repository("@@PROJECTTASKS", "projectTasks.item")
export class ProjectTaskItemsStore extends DataStore<ProjectTaskSummary> {
    baseUrl: string = ``;

    constructor() {
        super('PROJECTTASKITEM', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrlByProject(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}/approved`;
    }

    public changeUrlByProjectEmployee(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/Project/${companyId}`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectTaskSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectTaskSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async getProjectTask(query: Query, data?: any): Promise<QueryResult<any>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get<QueryResult<ProjectTaskSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data);
        return result.data;
    }
}


@repository("@@PROJECTTASKS", "projectTasks.dropdown")
export class ProjectTasksDropdownStore extends DataStore<ProjectTaskItemReference> {
    baseUrl: string = ``;

    constructor() {
        super('PROJECTTASKDROPDOWN', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container)
    }

    public changeUrlByProject(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}`
    }

    public changeUrlByEmployee(companyId: string, employeeId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/timesheet/${companyId}/${employeeId}/tasks`
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectTaskSummary>> {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectTaskSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))
        return result.data
    }
}

export class NewProjectTaskValidator extends Validator<NewProjectTaskItem> {
    constructor() {
        super();

        this.ruleFor(x => x.title)
            .notNull()
            .withMessage("Title required");

        this.ruleFor(x => x.projectId)
            .notNull()
            .withMessage("A valid ProjectId is required");
    }
}

export class ProjectTaskValidator extends Validator<ProjectTaskItem> {
    constructor() {
        super();

        this.ruleFor(x => x.title)
            .notNull()
            .withMessage("Title required");

        this.ruleFor(x => x.projectId)
            .notNull()
            .withMessage("A valid ProjectId is required");
    }
}

const PROJECTTASK_UPDATE_ITEM = "PROJECTTASK_UPDATE_ITEM";
@repository("@@PROJECTTASKS", "projectTasks.detail")
export class ProjectTaskStore extends FormStore<ProjectTaskItem> {
    baseUrl: string = ``;

    protected validate(item: ProjectTaskItem) {
        return new ProjectTaskValidator().validate(item);
    }

    constructor() {
        super('PROJECTTASK', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
        this.addReducer(PROJECTTASK_UPDATE_ITEM, (): any => {
            return super.onPatch()
        }, 'AsyncAction');
    }

    public changeUrl(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}`;
    }

    public async getById(id: string, params?: any, companyId?: string): Promise<ProjectTaskItem> {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LOAD, httpService.get<ProjectTaskItem>(`${this.baseUrl}/${companyId}/${id}`, params));
        return result.data;
    }

    public async update(item: ProjectTaskItem) {
        var result = await super.patch(PROJECTTASK_UPDATE_ITEM, `${item.id}`, item) as any;
        return result.data as CommandResult<ProjectTaskItem>;
    }
}

@repository("@@PROJECTTASKS", "projectTasks.new")
export class NewProjectTaskStore extends FormStore<NewProjectTaskItem> {
    baseUrl: string = ``;

    protected validate(item: NewProjectTaskItem) {
        return (new NewProjectTaskValidator()).validate(item);
    }

    constructor() {
        super('NEW_PROJECTTASK', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    public changeUrl(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}`;
    }

    public changeProjectlUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`;
    }

    public postSendEmailManager(titleTask: string, projectId: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        return httpService.post(`${this.baseUrl}/${titleTask}/${projectId}`, this.state.item);
    }

    public changePutUrl(companyId: string, projectId: string, taskId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}/${taskId}`;
    }

    public async update(item: ProjectTaskSummary) {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.put(`${this.baseUrl}`, item);

        return result.data;
    }   
}


export class TaskCollectionValidator extends Validator<NewTaskCollectionItem> {
    constructor() {
        super();
    }
}

@repository("@@PROJECTTASKS", "projectTasks.bulkNew")
export class NewProjectTasksFromTemplates extends FormStore<NewTaskCollectionItem> {
    baseUrl: string = ``;

    constructor() {
        super('NEW_PROJECTTASKS', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    protected validate(item: NewTaskCollectionItem) {
        return (new TaskCollectionValidator()).validate(item);
    }

    public changeUrl(companyId: string, projectId: string | undefined) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/${projectId}/from-templates`;
    }

    public changeUrlMasiveSave(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/tasks/${companyId}/from-templates-masive`;
    }

    public postList() {
        let httpService = this.container.get<HttpService>(HttpService);
        return httpService.post(`${this.baseUrl}`, this.state.item)
    }

    public postMasiveSave(items: SaveObject[]) {
        let httpService = this.container.get<HttpService>(HttpService);
        return httpService.post(`${this.baseUrl}`, items)
    }
}